import {
  POSTHOG_HOST,
  POSTHOG_PROJECT_TOKEN,
  POSTHOG_UI_HOST,
  environment,
} from "@/config";
import { isExpectedPostHogException } from "@/services/expected-error";

import posthog from "posthog-js";

// Never initialized during server rendering (app/src/entry-server.jsx).
const posthogEnabled =
  typeof window !== "undefined" &&
  environment === "production" &&
  Boolean(POSTHOG_PROJECT_TOKEN && POSTHOG_HOST && POSTHOG_UI_HOST);

export function sanitizePostHogUrl(value) {
  if (!value) return value;

  try {
    const url = new URL(value, window.location.origin);
    [
      "access_token",
      "auth",
      "code",
      "email",
      "key",
      "refresh_token",
      "secret",
      "session_id",
      "token",
    ].forEach((key) => {
      if (url.searchParams.has(key)) url.searchParams.set(key, "[REDACTED]");
    });
    if (
      url.searchParams.has("redirect") &&
      /(access_token|auth|code|email|key|refresh_token|secret|session_id|token)(=|%3D)/i.test(
        url.searchParams.get("redirect")
      )
    ) {
      url.searchParams.set("redirect", "[REDACTED]");
    }
    return url.toString();
  } catch {
    return String(value).replace(
      /([?&](access_token|auth|code|email|key|refresh_token|secret|session_id|token)=)[^&]+/gi,
      "$1[REDACTED]"
    );
  }
}

if (posthogEnabled) {
  posthog.init(POSTHOG_PROJECT_TOKEN, {
    api_host: POSTHOG_HOST,
    ui_host: POSTHOG_UI_HOST,
    defaults: "2026-05-30",
    capture_pageview: "history_change",
    person_profiles: "identified_only",
    capture_performance: true,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    disable_session_recording: false,
    enable_recording_console_log: false,
    loaded: (posthogInstance) => posthogInstance.startSessionRecording(true),
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: sanitizePostHogUrl(request.name),
      }),
    },
    before_send: (event) => {
      if (event.event === "$exception" && isExpectedPostHogException(event))
        return null;

      return {
        ...event,
        properties: {
          ...event.properties,
          $current_url: sanitizePostHogUrl(event.properties?.$current_url),
          $referrer: sanitizePostHogUrl(event.properties?.$referrer),
        },
      };
    },
  });
}

export { posthogEnabled };
export default posthog;
